import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isPandaInSearchEnabled } from '../../store/selectors/features.selector';
import { receiveAIV2Handoff } from '../../store/actions/ai-v2.actions';
import { usePandaChatStore } from '../chat/panda-chat-store.hooks';
import type { CreatePandaSearchAgent } from '../agent/panda-agent';
import type { PandaAnalytics } from '../analytics/panda-analytics';
import { useSearchAnswerRun } from './use-search-answer-run';
import type {
  OpenInChatOptions,
  SearchAnswerSubmission,
  UseSearchAnswerRun,
  UseSearchAnswerRunOptions,
} from './use-search-answer-run';

export interface UsePandaSearchAnswerOptions {
  submission: SearchAnswerSubmission | null;
  /** The search page's session id — joins the card's events to the page's. */
  contextId?: string;
  createAgent: CreatePandaSearchAgent;
  analytics: PandaAnalytics;
  /** False holds the run; the feature gate applies either way. */
  enabled?: boolean;
  onRunEnded?: UseSearchAnswerRunOptions['onRunEnded'];
  liveRun?: UseSearchAnswerRunOptions['liveRun'];
  /** Fires as answer text appears on / leaves the page — the "no results" copy softens on it. */
  onHasAnswerChange?: (hasAnswer: boolean) => void;
}

export interface PandaSearchAnswer extends UseSearchAnswerRun {
  /** Trace id stands in for a message id: this answer is never saved. */
  messageId: string;
  /**
   * Opens the answer in full Panda; the card stays, and a repeat click reopens
   * the same conversation. Starts no run: the thread is persisted only once the
   * user asks something there. Hosts still open their own chat surface.
   * Returns the chat's conversation id — the clicked event's join to AI_CHAT.
   */
  showMore: (options?: OpenInChatOptions) => string;
}

/**
 * `useSearchAnswerRun` plus the parts both hosts had copied: the feature gate, the
 * answer-presence callback, and the handoff dispatch. Hosts supply only their own
 * agent factory and analytics, and render.
 */
export function usePandaSearchAnswer({
  submission,
  contextId,
  createAgent,
  analytics,
  enabled: hostEnabled = true,
  onRunEnded,
  liveRun,
  onHasAnswerChange,
}: UsePandaSearchAnswerOptions): PandaSearchAnswer {
  const dispatch = useDispatch();
  // AI + panda-v2 must be on too: "show more" opens a V2 surface.
  const { isChatEnabled } = usePandaChatStore();
  const gateEnabled = useSelector(isPandaInSearchEnabled) && isChatEnabled;
  const enabled = hostEnabled && gateEnabled;

  const answer = useSearchAnswerRun({
    submission,
    contextId,
    enabled,
    createAgent,
    analytics,
    onRunEnded,
    liveRun,
  });
  const { hasAnswer, traceId, threadId, openInChat } = answer;

  useEffect(() => {
    onHasAnswerChange?.(hasAnswer);
  }, [hasAnswer, onHasAnswerChange]);

  // A still-streaming run is handed over live (see openInChat).
  const showMore = useCallback(
    (options?: OpenInChatOptions): string => {
      const conversationId = openInChat(options);
      dispatch(receiveAIV2Handoff({ conversationId }));
      return conversationId;
    },
    [openInChat, dispatch],
  );

  return { ...answer, messageId: traceId ?? threadId, showMore };
}
